import { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Trash2, PlayCircle, X, AlertTriangle, CheckCircle2, RotateCw } from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { quarantineApi, type QuarantinedEvent } from '@/api/quarantine.api';

const inputCls =
  'w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal';

type Action = 'discard' | 'override';
// FAILED = refused by myLedger (fix the cause, then Retry). QUARANTINED = held duplicate (a decision).
type Tab = 'failed' | 'quarantined';

export const QuarantinePage = () => {
  const { hasPermission } = usePermission();
  const canDiscard  = hasPermission(Permissions.INTEGRATION_POSTING_CANCEL);
  const canOverride = hasPermission(Permissions.INTEGRATION_DUPLICATE_OVERRIDE);
  const canRetry    = hasPermission(Permissions.INTEGRATION_POSTING_RETRY);

  const [tab, setTab]         = useState<Tab>('failed');
  const [retrying, setRetrying] = useState<string | null>(null);

  const [rows, setRows]       = useState<QuarantinedEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [notice, setNotice]   = useState<string | null>(null);

  // Resolution modal — both actions demand a reason, so neither can be a bare button.
  const [target, setTarget] = useState<QuarantinedEvent | null>(null);
  const [action, setAction] = useState<Action>('discard');
  const [reason, setReason] = useState('');
  const [busy, setBusy]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await (tab === 'failed' ? quarantineApi.listFailed() : quarantineApi.list()));
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to load the list.');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const open = (row: QuarantinedEvent, a: Action) => {
    setTarget(row); setAction(a); setReason(''); setError(null);
  };

  const retry = async (row: QuarantinedEvent) => {
    setRetrying(row.eventId);
    setError(null);
    setNotice(null);
    try {
      await quarantineApi.retry(row.eventId);
      setNotice(`Posted ${row.sourceDocumentNumber ?? row.eventId}.`);
      await load();
    } catch (err: unknown) {
      setError(`${row.sourceDocumentNumber ?? row.eventId} still does not post: `
        + ((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'the retry failed.'));
    } finally {
      setRetrying(null);
    }
  };

  const submit = async () => {
    if (!target || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (action === 'discard') {
        await quarantineApi.discard(target.eventId, reason.trim());
        setNotice(`Discarded ${target.sourceDocumentNumber ?? target.eventId} — it will not be posted.`);
      } else {
        await quarantineApi.overrideDuplicate(target.eventId, reason.trim());
        setNotice(`Posted ${target.sourceDocumentNumber ?? target.eventId} despite the duplicate.`);
      }
      setTarget(null);
      await load();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'The action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <ShieldAlert size={20} className="text-signal" /> Integration Exceptions
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {tab === 'failed'
            ? 'Documents myLedger refused to post. Fix the reason shown (map the account, enable the rule, open the period), then Retry. Discard one that should never post.'
            : 'Events held back for a decision. Nothing here is broken and retrying changes nothing — each one would raise a second liability for a supplier invoice number that already exists. Discard it, or override and post it.'}
        </p>
        <div className="flex gap-1 mt-3">
          {([['failed', 'Failed'], ['quarantined', 'Held duplicates']] as const).map(([k, label]) => (
            <button key={k} onClick={() => { setTab(k); setNotice(null); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg ${tab === k
                ? 'bg-signal text-white'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {notice && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> {notice}
          </div>
        )}
        {error && !target && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-12 text-center">
            <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{tab === 'failed' ? 'No failed documents.' : 'Nothing held.'}</p>
            <p className="text-xs text-slate-400 mt-1">Every integration event has either posted or been resolved.</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Source document</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Event</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Business date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">{tab === 'failed' ? 'Why it failed' : 'Why it is held'}</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map(r => (
                  <tr key={r.eventId} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 align-top">
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-900 dark:text-white">
                      {r.sourceDocumentNumber ?? '—'}
                      <div className="text-[11px] text-slate-400 font-sans">{r.sourceSystem}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium">{r.eventType}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                      {(r.businessDate ?? '').slice(0, 10)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-amber-700 dark:text-amber-400 max-w-xl">{r.reason}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {tab === 'failed' && canRetry && (
                        <button onClick={() => void retry(r)} disabled={retrying === r.eventId}
                          className="mr-1 px-2 py-1 text-xs font-medium rounded hover:bg-signal/10 text-signal inline-flex items-center gap-1 disabled:opacity-50">
                          {retrying === r.eventId ? <Spinner size="sm" /> : <RotateCw size={13} />} Retry
                        </button>
                      )}
                      {canDiscard && (
                        <button onClick={() => open(r, 'discard')}
                          className="px-2 py-1 text-xs font-medium rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                          <Trash2 size={13} /> Discard
                        </button>
                      )}
                      {tab === 'quarantined' && canOverride && (
                        <button onClick={() => open(r, 'override')}
                          className="ml-1 px-2 py-1 text-xs font-medium rounded hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                          <PlayCircle size={13} /> Override &amp; post
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resolution modal — a reason is mandatory for both actions, and is recorded against the event. */}
      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                {action === 'discard' ? 'Discard event' : 'Override the duplicate and post'}
              </h2>
              <button onClick={() => setTarget(null)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-mono text-slate-700 dark:text-slate-200">{target.sourceDocumentNumber}</span> · {target.eventType}
              </div>
              <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                {target.reason}
              </div>

              {action === 'override' ? (
                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
                  This posts a <strong>second liability</strong> for a supplier reference the ledger already has.
                  Only do this if it is genuinely a different invoice. The reason and your user are recorded.
                </div>
              ) : (
                <div className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300">
                  The event will never be posted and will not be re-sent. This cannot be undone.
                </div>
              )}

              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Reason (required)</span>
                <textarea className={inputCls} rows={3} value={reason} onChange={e => setReason(e.target.value)}
                  placeholder={action === 'override'
                    ? 'e.g. Supplier reused the invoice number across financial years; confirmed distinct goods with the buyer.'
                    : tab === 'failed'
                      ? 'e.g. Test document posted in myPOS ONE by mistake; not a real transaction.'
                      : 'e.g. Duplicate re-send from myPOS; the invoice is already captured.'} />
              </label>

              {error && (
                <div className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1.5">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {error}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setTarget(null)}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                Cancel
              </button>
              <button onClick={submit} disabled={busy || !reason.trim()}
                className={`px-3 py-1.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5 ${
                  action === 'override' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {busy ? <Spinner size="sm" /> : action === 'override' ? <PlayCircle size={14} /> : <Trash2 size={14} />}
                {action === 'override' ? 'Override & post' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
